import { useMutation, useQuery, UseQueryOptions } from "react-query";
import HttpService from "../../services/http";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import { queryClient } from "../../providers/QueryProvider";
import { useTranslation } from "react-i18next";
import { UniversalCacheManager } from "../../utils/cacheManager";
import {
  PLAN_CHANGE_ROUTES,
  planChangeRefusal,
} from "../../services/planChange";
import { forgetOwnerData } from "../entity/ownerData";

const billingCache = new UniversalCacheManager();

export const SMS_CREDIT_PLAN_ID = "SUMOC_SMSGTO20USD";
export const WHATSAPP_CREDIT_PLAN_ID = "SUMOC_WAGTO20USD";

export const forgetOwnerPlans = (entityIdentification: string) => {
  billingCache.delete(`billing_plan_status_${entityIdentification}`);
  queryClient.invalidateQueries("owner-plans");
};

export const useGetOwnerPlans = (
  customer_identification: string,
  entityIdentification: string,
  disabled?: boolean
) => {
  return useQuery(
    ["owner-plans"],
    async () => {
      const cacheKey = `billing_plan_status_${entityIdentification}`;
      const cached = billingCache.get(cacheKey);

      if (cached) {
        return cached;
      }

      const res = await HttpService.get(
        `/${customer_identification}/${entityIdentification}/billing/plan-status`
      );

      const data = res?.data?.element;
      billingCache.setWithLongTTL(cacheKey, data);
      return data;
    },
    {
      enabled: !disabled,
      onError: (err: any) => {
        toast.error(err?.response?.data?.message || err?.message);
      },
    }
  );
};

export const useCancelPlanSubscription = () => {
  const { t } = useTranslation();

  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entityIdentification,
      selectedPlan,
      atPeriodEnd,
    }: {
      customer_identification: string;
      entityIdentification: string;
      selectedPlan: string;
      atPeriodEnd?: boolean;
      successMessage?: string;
    }) =>
      HttpService.post(
        atPeriodEnd
          ? PLAN_CHANGE_ROUTES.cancelAtPeriodEnd(
              customer_identification,
              entityIdentification,
              selectedPlan
            )
          : `/${customer_identification}/${entityIdentification}/billing/${selectedPlan}/cancel-subscription`,
        null
      ),
    {
      onSuccess: (res, variables) => {
        toast.success(
          variables.successMessage || t("dashboard.billings.toast-01")
        );

        forgetOwnerPlans(variables.entityIdentification);
        queryClient.invalidateQueries(["plan-cards", variables.selectedPlan]);
        queryClient.invalidateQueries([
          "plan-detailed-info",
          variables.selectedPlan,
        ]);
      },
      onError: (err) => {
        const reason = planChangeRefusal(err);
        toast.error(
          reason
            ? t(`dashboard.billings.planChange.refusals.${reason}`)
            : err.response?.data?.element?.details ||
                err.response?.data?.message ||
                err.message
        );
      },
    }
  );
};

export const useUpdateSubscriptionCard = () => {
  const { t } = useTranslation();

  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entityIdentification,
      selectedPlan,
      payment_method_id,
    }: {
      customer_identification: string;
      entityIdentification: string;
      selectedPlan: string;
      payment_method_id: string;
    }) =>
      HttpService.put(
        `/${customer_identification}/${entityIdentification}/billing/${selectedPlan}/${payment_method_id}/update-subscription-card`,
        null
      ),
    {
      onSuccess: (res, variables) => {
        toast.success(t("dashboard.billings.toast-02"));
        queryClient.invalidateQueries(["plan-cards", variables.selectedPlan]);
        queryClient.invalidateQueries([
          "plan-detailed-info",
          variables.selectedPlan,
        ]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useRemovePaymentMethod = () => {
  const { t } = useTranslation();

  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entityIdentification,
      selectedPlan,
      payment_method_id,
    }: {
      customer_identification: string;
      entityIdentification: string;
      selectedPlan: string;
      payment_method_id: string;
    }) =>
      HttpService.delete(
        `/${customer_identification}/${entityIdentification}/billing/${selectedPlan}/${payment_method_id}/remove-payment-method`
      ),
    {
      onSuccess: (res, variables) => {
        toast.success(t("dashboard.billings.toast-03"));

        queryClient.invalidateQueries(["plan-cards", variables.selectedPlan]);
        queryClient.invalidateQueries([
          "plan-detailed-info",
          variables.selectedPlan,
        ]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

const fetchVerifyPlans = (
  customerId: string,
  entityId: string,
  credentials?: { user_identification?: string; access_token?: string }
) => {
  const canAuthenticate = Boolean(
    credentials?.user_identification && credentials?.access_token
  );

  return HttpService.get(
    `/auth/${customerId}/${entityId}/verify-payment`,
    canAuthenticate
      ? {
          headers: {
            Authorization: `Basic ${btoa(
              `${credentials!.user_identification}:${credentials!.access_token}`
            )}`,
          },
        }
      : undefined
  );
};

export const useGetVerifyPlans = (
  customer_identification: string,
  entityIdentification: string,
  options?: UseQueryOptions<any, AxiosError<any, any>, any, [string]>
) => {
  return useQuery(
    ["verify-plans"],
    async () => {
      const res = await fetchVerifyPlans(
        customer_identification,
        entityIdentification
      );
      return res?.data?.element;
    },
    {
      enabled:
        Boolean(customer_identification) && Boolean(entityIdentification),
      onError: (err: any) => {
        if (err?.response?.status === 401) {
          window.location.href = "/logout";
          return;
        }
        toast.error(err?.response?.data?.message || err?.message);
      },
      ...options,
    }
  );
};

export const useGetVerifyPlansMutation = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      user_identification,
      entity_identification,
      access_token,
    }: {
      user_identification: string;
      entity_identification: string;
      access_token?: string;
    }) => {
      return fetchVerifyPlans(user_identification, entity_identification, {
        user_identification,
        access_token,
      });
    },
    {
      onSuccess: (value) => {
        queryClient.invalidateQueries("payment-status");
        queryClient.invalidateQueries("verify-plans");
      },
      onError: (err: any) => {
        if (err.response?.status === 401) {
          window.location.href = "/logout";
          return;
        }
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useFreeSubscription = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      user_identification,
      entity_identification,
      data,
      access_token,
    }: {
      user_identification: string;
      entity_identification: string;
      data: any;
      access_token?: string;
    }) =>
      HttpService.post(
        `/${user_identification}/${entity_identification}/billing/create-free-subscription`,
        data,
        access_token
          ? {
              headers: {
                Authorization: `Basic ${btoa(
                  `${user_identification}:${access_token}`
                )}`,
              },
            }
          : undefined
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("verify-plans");
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useRequestInvoiceHistoryDetails = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({ invoiceId }: { invoiceId: string }) =>
      HttpService.get(`/billing/${invoiceId}/invoice`),
    {
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useSubscriptionCardDetail = () => {
  const { t } = useTranslation();

  return useMutation<
    { planCards: any[]; selectedActivePlanDefaultCardId: string },
    AxiosError<any>,
    {
      customer_identification: string;
      entityIdentification: string;
      plan: string;
    }
  >(
    async ({ customer_identification, entityIdentification, plan }) => {
      const value = await HttpService.get(
        `/${customer_identification}/${entityIdentification}/billing/${plan}/subscription-payment-card-detail`
      );

      let planCards: any[] = [];
      let selectedActivePlanDefaultCardId = "";

      if (value?.data?.element?.length > 0) {
        planCards = value.data.element.map((cardDetail: any) => {
          const { expMonth, expYear, ...rest } = cardDetail;
          return {
            ...rest,
            expDate: `${String(expMonth).padStart(2, "0")}/${expYear}`,
            expMonth,
            expYear,
          };
        });

        try {
          const detailedInfo = await HttpService.get(
            `/${customer_identification}/${entityIdentification}/billing/${plan}/plan-detailed-info`
          );
          if (detailedInfo.data?.element?.default_payment_method) {
            selectedActivePlanDefaultCardId =
              detailedInfo.data.element.default_payment_method;
          }
        } catch (err: any) {
          toast.error(err?.response?.data?.message || err?.message);
        }
      } else {
        toast.info(t("dashboard.billings.toast-04"));
      }

      return {
        planCards,
        selectedActivePlanDefaultCardId,
      };
    },
    {
      onError: (err) => {
        toast.error(err?.response?.data?.message || err?.message);
      },
    }
  );
};

/** A signed link to PayMod's card update page for one of the owner's plans, valid a few days. */
export const useCardUpdateLink = () =>
  useMutation<
    { url: string; expires_at: string },
    AxiosError<any>,
    {
      customer_identification: string;
      entityIdentification: string;
      plan: string;
    }
  >(
    async ({ customer_identification, entityIdentification, plan }) =>
      (
        await HttpService.get(
          `/${customer_identification}/${entityIdentification}/billing/${plan}/card-update-link`,
          {
            params: {
              redirect_path: "/control-panel/billing",
            },
          }
        )
      ).data.element,
    {
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );

export const useSubscriptionDetail = () => {
  return useMutation<
    {
      customer_identification: string;
      entityIdentification: string;
      selectedPlan: string;
    },
    AxiosError<any>,
    any
  >(
    ({
      customer_identification,
      entityIdentification,
      selectedPlan,
    }: {
      customer_identification: string;
      entityIdentification: string;
      selectedPlan: string;
    }) =>
      HttpService.get(
        `/${customer_identification}/${entityIdentification}/billing/${selectedPlan}/subscription-detail`
      ),
    {
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export type CreditKind = "sms" | "whatsapp";

export type CreditSyncResult = {
  sms_added: number;
  whatsapp_added: number;
};

type CreditSyncVariables = {
  customer_identification: string;
  entity_identification: string;
  kinds: CreditKind[];
};

const reconcileCredit = async (
  customer_identification: string,
  entity_identification: string,
  kind: CreditKind
) => {
  const plan = kind === "sms" ? SMS_CREDIT_PLAN_ID : WHATSAPP_CREDIT_PLAN_ID;
  const endpoint =
    kind === "sms"
      ? "verify-sms-credit-payment"
      : "verify-whatsapp-credit-payment";

  const { data } = await HttpService.post(
    `/${customer_identification}/${entity_identification}/billing/${plan}/${endpoint}`,
    {}
  );

  return Number(data?.element?.credits_added) || 0;
};

/**
 * Replays the credit reconciliation for payments the browser redirect or the
 * PayMod webhook never brought back. The server dedupes on the PayMod payment
 * id, so replaying it credits nothing twice. A kind that cannot be reconciled,
 * a plan never bought above all, is left out rather than reported: PayMod gives
 * no code to tell that apart from a real failure, and the server logs it.
 */
export const useSyncCreditPayments = () =>
  useMutation<CreditSyncResult, AxiosError<any>, CreditSyncVariables>(
    async ({ customer_identification, entity_identification, kinds }) => {
      const result: CreditSyncResult = { sms_added: 0, whatsapp_added: 0 };

      for (const kind of kinds) {
        try {
          const added = await reconcileCredit(
            customer_identification,
            entity_identification,
            kind
          );
          if (kind === "sms") result.sms_added = added;
          else result.whatsapp_added = added;
        } catch (error) {
          console.warn(`Credit sync skipped for ${kind}`, error);
        }
      }

      return result;
    },
    {
      onSuccess: (_value, variables) => {
        forgetOwnerData(
          variables.customer_identification,
          variables.entity_identification,
          "sms_credit"
        );
        forgetOwnerData(
          variables.customer_identification,
          variables.entity_identification,
          "whatsapp_credit"
        );
        queryClient.invalidateQueries([
          "whatsapp-config",
          variables.entity_identification,
        ]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
